// Spaced Repetition System with specific review rules
export class SpacedRepetitionSystem {
  constructor() {
    // Intervals in days as specified
    this.intervals = [1, 3, 7, 14, 30];
    this.maxStage = this.intervals.length - 1; // Index 4 (30 days)
  }

  // Initialize a new card with default values
  initializeCard(card) {
    return {
      ...card,
      spacedRepetition: {
        currentStage: 0,
        nextReviewDate: this.getTodayString(),
        lastReviewed: null,
        earlyReviews: 0, // Track early reviews for stats
      }
    };
  }

  // Get today's date as string (YYYY-MM-DD)
  getTodayString() {
    return new Date().toISOString().split('T')[0];
  }

  // Check if a card is due for review
  isCardDue(card) {
    const today = this.getTodayString();
    return card.spacedRepetition.nextReviewDate <= today;
  }

  // Review a card with the specified rules
  reviewCard(card, userScore) {
    const today = this.getTodayString();
    const sr = card.spacedRepetition;
    
    // Always update last_reviewed date
    sr.lastReviewed = today;

    // Rule 1: If today < next_review_date (early review)
    if (today < sr.nextReviewDate) {
      sr.earlyReviews += 1;
      
      return {
        ...card,
        spacedRepetition: sr,
        reviewFeedback: {
          type: 'early',
          message: `Early review: schedule unchanged`,
          nextReviewDate: sr.nextReviewDate,
          daysUntilNext: this.getDaysUntil(sr.nextReviewDate)
        }
      };
    }

    // Rule 2: If today >= next_review_date (scheduled review)
    if (userScore < 100) {
      // Fail: Keep current_stage, keep same next_review_date (no change)
      
      return {
        ...card,
        spacedRepetition: sr,
        reviewFeedback: {
          type: 'fail',
          message: `Review incomplete: next review in ${this.getDaysUntil(sr.nextReviewDate)} days`,
          nextReviewDate: sr.nextReviewDate,
          daysUntilNext: this.getDaysUntil(sr.nextReviewDate)
        }
      };
    }

    if (userScore === 100) {
      // Success: Increment current_stage (cap at max), set next_review_date
      const newStage = Math.min(sr.currentStage + 1, this.maxStage);
      sr.currentStage = newStage;
      
      const nextReviewDate = this.addDays(today, this.intervals[newStage]);
      sr.nextReviewDate = nextReviewDate;
      
      return {
        ...card,
        spacedRepetition: sr,
        reviewFeedback: {
          type: 'success',
          message: `Perfect! Next review in ${this.intervals[newStage]} days`,
          nextReviewDate: nextReviewDate,
          daysUntilNext: this.intervals[newStage]
        }
      };
    }

    // Fallback (shouldn't happen with the rules)
    return card;
  }

  // Add days to a date string
  addDays(dateString, days) {
    const date = new Date(dateString);
    date.setDate(date.getDate() + days);
    return date.toISOString().split('T')[0];
  }

  // Get days until a date
  getDaysUntil(dateString) {
    const today = new Date();
    const targetDate = new Date(dateString);
    const diffTime = targetDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  }

  // Get cards that are due for review
  getCardsForReview(cards) {
    return cards.filter(card => {
      if (!card.spacedRepetition) {
        // New cards are always due
        return true;
      }
      return this.isCardDue(card);
    });
  }

  // Get cards that are not due yet (for early review)
  getCardsForEarlyReview(cards) {
    return cards.filter(card => {
      if (!card.spacedRepetition) {
        return false; // New cards should go through normal flow
      }
      return !this.isCardDue(card);
    });
  }

  // Get session statistics
  getSessionStats(cards) {
    const dueCards = this.getCardsForReview(cards);
    const earlyCards = this.getCardsForEarlyReview(cards);
    const newCards = cards.filter(card => !card.spacedRepetition);

    return {
      total: cards.length,
      due: dueCards.length,
      early: earlyCards.length,
      new: newCards.length,
      intervals: this.intervals
    };
  }

  // Get card status for display
  getCardStatus(card) {
    if (!card.spacedRepetition) {
      return 'new';
    }

    const today = this.getTodayString();
    const nextReview = card.spacedRepetition.nextReviewDate;

    if (nextReview <= today) {
      return 'due';
    } else {
      return 'scheduled';
    }
  }

  // Get current stage name
  getStageName(stage) {
    const stageNames = ['New', 'Learning', 'Familiar', 'Mastered', 'Expert'];
    return stageNames[stage] || 'Unknown';
  }

  // Get next interval for a card
  getNextInterval(card) {
    if (!card.spacedRepetition) {
      return this.intervals[0]; // First interval for new cards
    }
    return this.intervals[card.spacedRepetition.currentStage];
  }
}

// Export singleton instance
export const spacedRepetitionSystem = new SpacedRepetitionSystem();